<script setup>
/**
 * GoalForm
 * Form for creating and editing goals
 * Responsive layout for PC, tablet, and mobile
 */
import { ref, computed, onMounted } from 'vue'
import AppButton from '@/ui/components/AppButton.vue'
import { useGoals, GOAL_TYPES, MAKAT_TYPES, createNewGoal } from '@/core/composables/useGoals'
import { validateGoalForm } from '@/core/utils/goalHelpers'
import { useToast } from '@/core/composables/useToast'

const props = defineProps({
  editGoal: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close', 'saved'])

const { success, error: toastError } = useToast()
const { loading, createGoal, updateGoal, validateAndFetchProducts, fetchAgents } = useGoals()

// Form state
const goal = ref(createNewGoal())
const tempMakats = ref('')
const isSubmitting = ref(false)
const isLoadingAgents = ref(false)

// Computed
const isEditMode = computed(() => !!props.editGoal?.uid)
const formTitle = computed(() => (isEditMode.value ? 'עריכת יעד' : 'יצירת יעד חדש'))
const hasProducts = computed(() => goal.value.products.length > 0)
const hasAgents = computed(() => goal.value.agents.length > 0)

// Initialize form
onMounted(async () => {
  if (props.editGoal) {
    goal.value = { ...props.editGoal }
    if (goal.value.includedSahar === undefined) {
      goal.value.includedSahar = true
    }
  } else {
    await loadAgents()
  }
})

// Load agents for new goal
async function loadAgents() {
  isLoadingAgents.value = true
  try {
    const agents = await fetchAgents()
    goal.value.agents = agents
  } catch (err) {
    toastError('שגיאה בטעינת סוכנים')
    console.error(err)
  } finally {
    isLoadingAgents.value = false
  }
}

// Check/validate makats
async function checkMakats() {
  if (!tempMakats.value.trim()) {
    toastError('נא למלא מספרי מקטים')
    return
  }

  try {
    const { products, error } = await validateAndFetchProducts(
      tempMakats.value,
      goal.value.makat_type
    )

    if (error) {
      toastError(error)
      return
    }

    goal.value.products = products
    success(`נמצאו ${products.length} מוצרים`)

    if (goal.value.agents.length === 0) {
      await loadAgents()
    }
  } catch (err) {
    toastError('שגיאה בבדיקת מקטים')
    console.error(err)
  }
}

// Submit form
async function submitForm() {
  const { isValid, error } = validateGoalForm(goal.value)
  if (!isValid) {
    toastError(error)
    return
  }

  isSubmitting.value = true

  try {
    if (isEditMode.value) {
      await updateGoal(goal.value.uid, goal.value)
      success('היעד עודכן בהצלחה!')
    } else {
      await createGoal(goal.value)
      success('היעד נוצר בהצלחה!')
    }

    emit('saved')
    emit('close')
  } catch (err) {
    toastError('שגיאה בשמירת היעד')
    console.error(err)
  } finally {
    isSubmitting.value = false
  }
}

// Select goal type
function selectGoalType(type) {
  goal.value.type = type
}

// Select makat type
function selectMakatType(type) {
  goal.value.makat_type = type
  goal.value.products = []
  tempMakats.value = ''
}

// Toggle active status
function toggleActive(active) {
  goal.value.active = active
}

// Remove product
function removeProduct(makat) {
  goal.value.products = goal.value.products.filter((p) => p.makat !== makat)
}

// Handle image error
function handleImageError(event) {
  event.target.src = '/images/placeholder-product.png'
}

// Handle agent image error
function handleAgentImageError(event) {
  event.target.src = '/images/default-profile.png'
}

// Get product image
function getProductImage(product) {
  if (product.db_image?.img) return product.db_image.img
  if (product.image) return product.image
  return '/images/placeholder-product.png'
}
</script>

<template>
  <div class="goal-form-wrapper">
    <div class="goal-form">
      <!-- Header -->
      <header class="form-header">
        <h1 class="form-title">{{ formTitle }}</h1>
        <p class="form-subtitle">{{ isEditMode ? 'עדכן את פרטי היעד' : 'הגדר יעד חדש לסוכנים' }}</p>
      </header>

      <!-- Main Form Content -->
      <div class="form-content">
        <!-- Basic Info Section -->
        <section class="form-section">
          <h2 class="section-title">פרטי היעד</h2>

          <div class="form-grid">
            <!-- Goal Title -->
            <div class="form-group full-width">
              <label class="form-label">שם היעד</label>
              <input
                v-model="goal.title"
                type="text"
                class="form-input"
                placeholder="לדוגמה: יעד מכירות Q1"
              />
            </div>

            <!-- Goal Type Selection -->
            <div class="form-group full-width">
              <label class="form-label">סוג היעד</label>
              <div class="type-buttons">
                <button
                  v-for="type in [
                    GOAL_TYPES.QUANTITY,
                    GOAL_TYPES.DISTRIBUTION,
                    GOAL_TYPES.FINANCIAL,
                  ]"
                  :key="type"
                  type="button"
                  class="type-btn"
                  :class="{ selected: goal.type === type }"
                  @click="selectGoalType(type)"
                >
                  <span class="type-icon">
                    <svg
                      v-if="type === GOAL_TYPES.QUANTITY"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <svg
                      v-else-if="type === GOAL_TYPES.DISTRIBUTION"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                    <svg
                      v-else
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <line x1="12" y1="1" x2="12" y2="23" />
                      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                    </svg>
                  </span>
                  <span class="type-label">{{ type }}</span>
                  <span v-if="goal.type === type" class="type-check">
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            <!-- Status & Options Row -->
            <div class="form-group">
              <label class="form-label">סטטוס</label>
              <div class="status-buttons">
                <button
                  type="button"
                  class="status-btn"
                  :class="{ active: goal.active }"
                  @click="toggleActive(true)"
                >
                  <span class="status-dot active"></span>
                  פעיל
                </button>
                <button
                  type="button"
                  class="status-btn"
                  :class="{ inactive: !goal.active }"
                  @click="toggleActive(false)"
                >
                  <span class="status-dot inactive"></span>
                  לא פעיל
                </button>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">אפשרויות</label>
              <label class="checkbox-label">
                <input v-model="goal.includedSahar" type="checkbox" class="checkbox-input" />
                <span class="checkbox-custom"></span>
                <span class="checkbox-text">נכלל באשף שכר</span>
              </label>
            </div>
          </div>
        </section>

        <!-- Products Section -->
        <section class="form-section">
          <h2 class="section-title">מוצרים</h2>

          <div class="form-grid">
            <!-- Makat Type Selection -->
            <div class="form-group full-width">
              <label class="form-label">שיוך מוצרים</label>
              <div class="makat-type-buttons">
                <button
                  type="button"
                  class="makat-btn"
                  :class="{ selected: goal.makat_type === MAKAT_TYPES.INDIVIDUAL }"
                  @click="selectMakatType(MAKAT_TYPES.INDIVIDUAL)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  מקטים נפרדים
                </button>
                <button
                  type="button"
                  class="makat-btn"
                  :class="{ selected: goal.makat_type === MAKAT_TYPES.CENTER }"
                  @click="selectMakatType(MAKAT_TYPES.CENTER)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  מקט מרכז
                </button>
              </div>
            </div>

            <!-- Makat Input -->
            <div v-if="goal.makat_type" class="form-group full-width">
              <label class="form-label">
                {{
                  goal.makat_type === MAKAT_TYPES.INDIVIDUAL
                    ? 'מקטים (מופרדים ברווחים)'
                    : 'מקט מרכז'
                }}
              </label>
              <div class="input-with-button">
                <input
                  v-model="tempMakats"
                  type="text"
                  class="form-input"
                  :placeholder="
                    goal.makat_type === MAKAT_TYPES.INDIVIDUAL ? '1001 1002 1003' : '5000'
                  "
                  @keyup.enter="checkMakats"
                />
                <AppButton variant="primary" :loading="loading" @click="checkMakats">
                  חפש
                </AppButton>
              </div>
            </div>
          </div>

          <!-- Found Products Display -->
          <div v-if="hasProducts" class="products-grid">
            <div v-for="product in goal.products" :key="product.makat" class="product-card">
              <button type="button" class="product-remove" @click="removeProduct(product.makat)">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <div class="product-image">
                <img
                  :src="getProductImage(product)"
                  :alt="product.name"
                  @error="handleImageError"
                />
              </div>
              <div class="product-info">
                <span class="product-makat">{{ product.makat }}</span>
                <span class="product-name">{{ product.name }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Agents Section -->
        <section v-if="hasProducts" class="form-section">
          <h2 class="section-title">
            יעדי סוכנים
            <span v-if="hasAgents" class="section-count">{{ goal.agents.length }} סוכנים</span>
          </h2>

          <div v-if="isLoadingAgents" class="loading-placeholder">
            <div class="spinner"></div>
            <span>טוען סוכנים...</span>
          </div>

          <div v-else-if="hasAgents" class="agents-grid">
            <div v-for="agent in goal.agents" :key="agent.uid" class="agent-card">
              <div class="agent-avatar">
                <img
                  :src="agent.image || '/images/default-profile.png'"
                  :alt="agent.name"
                  @error="handleAgentImageError"
                />
                <span class="agent-badge">{{ agent.id }}</span>
              </div>
              <div class="agent-details">
                <span class="agent-name">{{ agent.name }}</span>
                <div class="agent-goal-input-wrapper">
                  <input
                    v-model.number="agent.goal"
                    type="number"
                    class="agent-goal-input"
                    placeholder="0"
                    min="0"
                  />
                  <span class="input-suffix">
                    {{
                      goal.type === GOAL_TYPES.FINANCIAL
                        ? '₪'
                        : goal.type === GOAL_TYPES.DISTRIBUTION
                          ? 'לקוחות'
                          : "יח'"
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <p>לא נמצאו סוכנים</p>
          </div>
        </section>

        <!-- Dates Section -->
        <section v-if="hasProducts" class="form-section">
          <h2 class="section-title">תקופת היעד</h2>

          <div class="dates-grid">
            <div class="form-group">
              <label class="form-label">תאריך התחלה</label>
              <input v-model="goal.date_start" type="date" class="form-input date-input" />
            </div>
            <div class="form-group">
              <label class="form-label">תאריך סיום</label>
              <input v-model="goal.date_end" type="date" class="form-input date-input" />
            </div>
          </div>
        </section>
      </div>

      <!-- Form Actions -->
      <footer class="form-footer">
        <AppButton variant="secondary" size="lg" @click="emit('close')"> ביטול </AppButton>
        <AppButton variant="primary" size="lg" :loading="isSubmitting" @click="submitForm">
          {{ isEditMode ? 'עדכן יעד' : 'צור יעד' }}
        </AppButton>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.goal-form-wrapper {
  width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;
  padding: var(--spacing-md);
}

.goal-form {
  width: 100%;
  max-width: 900px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
  overflow: hidden;
}

/* Header */
.form-header {
  padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-lg);
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  border-bottom: 1px solid var(--border-color);
  text-align: center;
}

.form-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.form-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* Content */
.form-content {
  padding: var(--spacing-lg);
}

/* Sections */
.form-section {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
  border-bottom: 1px solid var(--border-color);
}

.form-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
}

.section-count {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
  background: var(--bg-tertiary);
  padding: 2px var(--spacing-sm);
  border-radius: var(--border-radius-sm);
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.form-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input::placeholder {
  color: var(--text-muted);
}

.date-input {
  cursor: pointer;
}

/* Type Buttons */
.type-buttons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-sm);
}

.type-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.type-btn:hover {
  border-color: var(--color-primary);
  background: var(--bg-tertiary);
}

.type-btn.selected {
  border-color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  color: var(--color-primary);
}

.type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius);
}

.type-btn.selected .type-icon {
  background: var(--color-primary);
  color: white;
}

.type-btn.selected .type-icon svg {
  stroke: white;
}

.type-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.type-check {
  position: absolute;
  top: var(--spacing-xs);
  left: var(--spacing-xs);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-success);
  border-radius: 50%;
  color: white;
}

/* Status Buttons */
.status-buttons {
  display: flex;
  gap: var(--spacing-sm);
}

.status-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.status-btn.active {
  background: rgba(16, 185, 129, 0.1);
  border-color: var(--color-success);
  color: var(--color-success);
}

.status-btn.inactive {
  background: rgba(239, 68, 68, 0.1);
  border-color: var(--color-error);
  color: var(--color-error);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--text-muted);
}

.status-dot.active {
  background: var(--color-success);
}

.status-dot.inactive {
  background: var(--color-error);
}

/* Checkbox */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.checkbox-label:hover {
  background: var(--bg-tertiary);
}

.checkbox-input {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.checkbox-input:checked + .checkbox-custom {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.checkbox-input:checked + .checkbox-custom::after {
  content: '';
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

.checkbox-text {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

/* Makat Type Buttons */
.makat-type-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-sm);
}

.makat-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.makat-btn:hover {
  border-color: var(--color-primary);
}

.makat-btn.selected {
  border-color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  color: var(--color-primary);
}

/* Input with Button */
.input-with-button {
  display: flex;
  gap: var(--spacing-sm);
}

.input-with-button .form-input {
  flex: 1;
}

/* Products Grid */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
}

.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
}

.product-remove {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-error);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.product-card:hover .product-remove {
  opacity: 1;
}

.product-image {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-xs);
}

.product-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: var(--border-radius-sm);
}

.product-info {
  text-align: center;
}

.product-makat {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
}

.product-name {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

/* Agents Grid */
.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--spacing-sm);
}

.agent-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.agent-card:hover {
  border-color: var(--color-primary);
}

.agent-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.agent-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: var(--bg-tertiary);
}

.agent-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  background: var(--color-primary);
  border-radius: 10px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  color: white;
}

.agent-details {
  flex: 1;
  min-width: 0;
}

.agent-name {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-goal-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.agent-goal-input {
  width: 80px;
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--bg-primary);
  color: var(--text-primary);
  text-align: center;
}

.agent-goal-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.input-suffix {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

/* Dates Grid */
.dates-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
}

/* Loading & Empty States */
.loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

/* Footer */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg) var(--spacing-xl);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

/* Responsive */
@media (max-width: 768px) {
  .goal-form-wrapper {
    padding: var(--spacing-sm);
  }

  .form-header {
    padding: var(--spacing-lg);
  }

  .form-title {
    font-size: var(--font-size-xl);
  }

  .form-content {
    padding: var(--spacing-md);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .type-buttons {
    grid-template-columns: 1fr;
  }

  .makat-type-buttons {
    grid-template-columns: 1fr;
  }

  .dates-grid {
    grid-template-columns: 1fr;
  }

  .agents-grid {
    grid-template-columns: 1fr;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }

  .form-footer {
    flex-direction: column-reverse;
    padding: var(--spacing-md);
  }

  .form-footer .app-button {
    width: 100%;
  }
}

@media (min-width: 1200px) {
  .goal-form {
    max-width: 1000px;
  }

  .agents-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
